import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, Globe, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";

export default function AgentProfile() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/agents/${id}`).then(({ data }) => setData(data)).catch((e) => setError(e.response?.data?.detail || "Could not load agent"));
    }, [id]);

    if (error) return (
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
            <h2 className="font-display text-2xl font-bold mb-2">Agent not found</h2>
            <p className="text-muted-foreground font-app mb-6">{String(error)}</p>
            <Button asChild variant="outline"><Link to="/agents"><ArrowLeft className="h-4 w-4 mr-1" /> Back to all agents</Link></Button>
        </div>
    );
    if (!data) return null;

    const { agent, listings, stats } = data;
    return (
        <div data-testid="agent-profile-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="mb-6">
                <Link to="/agents"><ArrowLeft className="h-4 w-4 mr-1" /> All agents</Link>
            </Button>

            <Card className="p-6 mb-8">
                <div className="flex items-start gap-5 flex-wrap">
                    <div className="h-20 w-20 rounded-full bg-gradient-to-br from-fiji-green to-ocean text-white grid place-items-center font-display font-bold text-3xl flex-shrink-0">
                        {(agent.full_name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h1 className="font-display text-3xl font-bold">{agent.full_name}</h1>
                            {agent.agent_verified && (
                                <Badge className="bg-fiji-green/15 text-fiji-green border-0 text-xs uppercase font-app">
                                    <BadgeCheck className="h-3.5 w-3.5 mr-0.5" /> Verified Agent
                                </Badge>
                            )}
                        </div>
                        {agent.agency_name && <div className="font-app text-base font-medium text-muted-foreground">{agent.agency_name}</div>}
                        {agent.agency_website && (
                            <a href={agent.agency_website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-coral hover:underline font-app mt-1">
                                <Globe className="h-3 w-3" /> {agent.agency_website.replace(/^https?:\/\//, "")}
                            </a>
                        )}
                        {agent.agent_bio && <p className="font-app text-sm text-muted-foreground mt-3 max-w-2xl">{agent.agent_bio}</p>}
                        <div className="grid grid-cols-3 gap-4 mt-5 max-w-md">
                            <div><div className="font-display text-2xl font-bold tabular-nums">{stats.active_listings}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Listings</div></div>
                            <div><div className="font-display text-2xl font-bold tabular-nums text-coral">{stats.featured_listings}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Featured</div></div>
                            <div><div className="font-display text-2xl font-bold tabular-nums text-fiji-green">{stats.jv_eligible_listings}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Joint Venture-eligible</div></div>
                        </div>
                    </div>
                </div>
            </Card>

            <h2 className="font-display text-xl font-bold mb-4">Active listings</h2>
            {listings.length === 0 ? (
                <Card className="p-12 text-center">
                    <p className="font-app text-muted-foreground">No active listings right now.</p>
                </Card>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {listings.map((li) => <PropertyCard key={li.id} listing={li} />)}
                </div>
            )}
        </div>
    );
}
